import { initializeTable } from '../tables';
import { debounce } from '../../utils';
import { deletePrompt, postPrompt } from '../../prompts';
import { addAlert } from '../../alerts';

export default async function initializeWasteCollectRequestsTable({
    ...options
}) {
    const rootElement = document.querySelector('#wasteCollectRequestsTable');
    const isStaff = rootElement.dataset.isStaff === 'true';

    const [statuses, types] = await Promise.all([
        fetch('/api/wastes/waste_collect_requests/statuses/').then(
            (response) => {
                return response.json();
            },
        ),
        fetch('/api/wastes/waste_collect_requests/types/').then((response) => {
            return response.json();
        }),
    ]);

    const statusFormatter = statuses.reduce(
        (obj, item) => Object.assign(obj, { [item.value]: item.label }),
        {},
    );

    const typesFormatter = types.reduce(
        (obj, item) => Object.assign(obj, { [item.value]: item.label }),
        {},
    );

    const wasteTable = initializeTable({
        tableElement: rootElement,
        ajaxURL: rootElement.dataset.ajaxUrl,
        layout: 'fitColumns',
        columnDefaults: {
            minWidth: 120,
        },
        columns: [
            {
                title: gettext('#'),
                field: 'id',
                formatter: function (cell) {
                    const data = cell.getData();
                    if (data.table_notification) {
                        const status =
                            data.status == 6 || data.status == 7 ? 'red' : '';
                        return `${data.id} <span class="status-indicator ${status}"></span>`;
                    }
                    return data.id;
                },
                minWidth: 70,
                maxWidth: 100,
                resizable: false,
                cssClass: 'space-between',
            },
            {
                title: gettext('Status'),
                field: 'status',
                formatter: 'lookup',
                formatterParams: statusFormatter,
                resizable: false,
                minWidth: 225,
            },
            {
                title: gettext('Typ'),
                field: 'request_type',
                formatter: 'lookup',
                formatterParams: typesFormatter,
                resizable: false,
                maxWidth: 120,
            },
            {
                title: gettext('Data utworzenia'),
                field: 'creat_date',
                formatter: 'datetime',
                formatterParams: {
                    inputFormat: "yyyy-MM-dd'T'HH:mm:ss.SSS",
                    outputFormat: 'yyyy-MM-dd HH:mm',
                    invalidPlaceholder: gettext('Zły format daty'),
                    timezone: 'Poland',
                },
                resizable: false,
            },
            {
                title: gettext('Data modyfikacji'),
                field: 'change_date',
                formatter: 'datetime',
                formatterParams: {
                    inputFormat: "yyyy-MM-dd'T'HH:mm:ss.SSS",
                    outputFormat: 'yyyy-MM-dd HH:mm',
                    invalidPlaceholder: gettext('Zły format daty'),
                    timezone: 'Poland',
                },
                resizable: false,
            },
            {
                title: gettext('Data zgłoszenia'),
                field: 'request_date',
                formatter: 'datetime',
                formatterParams: {
                    inputFormat: "yyyy-MM-dd'T'HH:mm:ss",
                    outputFormat: 'yyyy-MM-dd HH:mm',
                    invalidPlaceholder: gettext('Zły format daty'),
                    timezone: 'Poland',
                },
                resizable: false,
            },
            {
                title: isStaff ? gettext('Akcja') : gettext('Akcje'),
                field: 'actions',
                resizable: false,
                headerSort: false,
                frozen: window.innerWidth < 768,
                minWidth:
                    window.innerWidth < 768
                        ? isStaff
                            ? 62
                            : 70
                        : isStaff
                          ? 80
                          : 150,
                maxWidth: isStaff ? 80 : 150,
                formatter: function (cell, formatterParams, onRendered) {
                    const data = cell.getRow().getData();
                    const isStaff = rootElement.dataset.isStaff === 'true';

                    let primaryAction = {
                        text: gettext('Podgląd'),
                        url: `/wastes/request/detail/${data.id}/`,
                    };
                    let secondaryAction = null;

                    if (!isStaff) {
                        if (data.status === 0) {
                            primaryAction = {
                                text: gettext('Edytuj'),
                                url:
                                    data.request_type === 1
                                        ? `/wastes/request/automatic/wastes/${data.id}/`
                                        : `/wastes/request/manual/${data.id}/`,
                            };
                            secondaryAction = {
                                text: gettext('Usuń'),
                                type: 'delete',
                                url: `/api/wastes/waste_collect_requests/${data.id}/delete/`,
                            };
                        } else {
                            if (data.status === 2) {
                                secondaryAction = {
                                    text: gettext('Anuluj'),
                                    type: 'cancel',
                                    url: `/api/wastes/waste_collect_requests/${data.id}/cancel/`,
                                };
                            }
                        }
                    }

                    onRendered(function () {
                        const el = cell.getElement();
                        const triggerBtn = el.querySelector('.trigger-button');
                        if (triggerBtn) {
                            triggerBtn.addEventListener('click', function (e) {
                                e.stopPropagation();
                                e.preventDefault();
                                const actionType =
                                    triggerBtn.dataset.actionType;
                                const url = triggerBtn.dataset.url;

                                let promptTitle = gettext('Potwierdzenie');
                                let promptHtml = '';
                                let isDelete = false;

                                if (actionType === 'delete') {
                                    promptHtml = `${gettext('Czy na pewno chcesz usunąć wniosek nr. ')}${cell.getRow().getData().id}?<br/><small>${gettext('Tak operacja jest nieodwracalna!')}</small>`;
                                    isDelete = true;
                                } else if (actionType === 'cancel') {
                                    promptHtml = `${gettext('Czy na pewno chcesz anulować wniosek nr. ')}${cell.getRow().getData().id}?<br/><small>${gettext('Tak operacja jest nieodwracalna!')}</small>`;
                                }

                                const handleSuccess = function (response) {
                                    if (response?.data?.message) {
                                        addAlert(
                                            'success',
                                            response.data.message,
                                        );
                                    }
                                    cell.getTable().setData();
                                };

                                if (isDelete) {
                                    deletePrompt({
                                        options: {
                                            title: promptTitle,
                                            html: promptHtml,
                                            confirmText: gettext('Tak'),
                                        },
                                        url: url,
                                        successAction: handleSuccess,
                                    });
                                } else {
                                    postPrompt({
                                        options: {
                                            title: promptTitle,
                                            html: promptHtml,
                                            confirmText: gettext('Tak'),
                                        },
                                        url: url,
                                        successAction: handleSuccess,
                                    });
                                }
                            });
                        }
                    });

                    let html = `<div class="actions-cell ${secondaryAction ? 'grid-layout' : ''}">
                    <div class="action-slot slot-primary">
                    <a class="btn-link-action" href="${primaryAction.url}">${primaryAction.text}</a>
                    </div>`;
                    if (secondaryAction) {
                        html += `<div class="action-slot slot-secondary">
                        <button class="btn-link delete trigger-button" data-action-type="${secondaryAction.type}" data-url="${secondaryAction.url}">${secondaryAction.text}</button>
                        </div>`;
                    }
                    html += '</div>';
                    return html;
                },
            },
        ],
        rowFormatter: (row) => {
            const data = row.getData();
            if (data.table_notification) {
                const element = row.getElement();
                element.classList.add('tabulator-changed-row');
            }
        },
        ...options,
    });

    wasteTable.on('rowClick', function (e, row) {
        if (e.target.closest('a') || e.target.closest('button')) {
            return;
        }
        const data = row.getData();
        const isStaff = rootElement.dataset.isStaff === 'true';
        if (!isStaff && data.status === 0) {
            if (data.request_type === 1) {
                window.location.href = `/wastes/request/automatic/wastes/${data.id}/`;
            } else {
                window.location.href = `/wastes/request/manual/${data.id}/`;
            }
        } else {
            window.location.href = `/wastes/request/detail/${data.id}/`;
        }
    });

    function toggleFrozenColumn() {
        const smallScreen = window.innerWidth < 768;
        const col = wasteTable.getColumn('actions');
        if (col) {
            const isCurrentlyFrozen = Boolean(col.getDefinition().frozen);
            if (smallScreen !== isCurrentlyFrozen) {
                if (smallScreen) {
                    col.updateDefinition({
                        frozen: true,
                        minWidth: isStaff ? 62 : 70,
                    });
                } else {
                    col.updateDefinition({
                        frozen: false,
                        minWidth: isStaff ? 80 : 150,
                    });
                }
            }
        }
    }

    window.addEventListener('resize', debounce(toggleFrozenColumn, 200));
}
